import React, { useCallback, useMemo } from "react";
import { GlobalStore } from "../../../store/global/GlobalStore";
import "./GuildRole.css";
import { MODAL_SELECTORS } from "../../Modal/instance/modalSelectors";
import { WalletContentDisplayLocations } from "./ConnectedWalletButton";
import { Theme } from "styles/theme";
import { GUILD_ROLES_EMOJIS } from "config/consts";
import { useSelectedGuildRole } from "hooks/useSelectedGuildRole";

export interface GuildRoleProps {
  displayLocation: WalletContentDisplayLocations;
  isVisible?: boolean;
}

export type GuildClassMap = Record<"wrapper" | "span", string | undefined>;

export const GuildRole: React.FC<GuildRoleProps> = props => {
  const modal = GlobalStore.useState(s => s.modalSelector);
  const theme = GlobalStore.useState(s => s.theme);
  const selectedRole = useSelectedGuildRole();

  // Different left margins from header to sidenav from
  const guildClasses: Record<WalletContentDisplayLocations, GuildClassMap> = useMemo(() => {
    return {
      [WalletContentDisplayLocations.Sidenav]: { wrapper: "guild-title-header", span: undefined },
      [WalletContentDisplayLocations.Header]: {
        wrapper: "guild-title-header",
        span: "d-none d-md-block",
      },
      [WalletContentDisplayLocations.WalletView]: {
        wrapper: "guild-title-wallet",
        span: undefined,
      },
      [WalletContentDisplayLocations.NavbarV2]: { wrapper: undefined, span: undefined },
      [WalletContentDisplayLocations.NavbarV2Mobile]: { wrapper: undefined, span: undefined },
    };
  }, []);

  const getGuildStyles = useCallback(
    (
      theme: Theme
    ): Record<WalletContentDisplayLocations, Record<string, React.CSSProperties | undefined>> => {
      return {
        [WalletContentDisplayLocations.Sidenav]: {
          wrapper: undefined,
          span: { fontWeight: 500, color: "#CCD1D1", lineHeight: "16px", fontSize: "12px" },
        },
        [WalletContentDisplayLocations.Header]: { wrapper: {}, span: {} },
        [WalletContentDisplayLocations.WalletView]: {
          span: undefined,
          wrapper: { backgroundColor: theme.surface.alt.primary.default },
        },
        [WalletContentDisplayLocations.NavbarV2]: {
          wrapper: undefined,
          span: undefined,
        },
        [WalletContentDisplayLocations.NavbarV2Mobile]: {
          wrapper: undefined,
          span: {
            color: theme.font.secondary.default,
            fontSize: theme.fontSize[4],
            lineHeight: theme.lineHeights["4"],
          },
        },
      };
    },
    []
  );

  const guildStyles = getGuildStyles(theme);

  const Wrapper = useMemo(() => {
    if (
      guildClasses[props.displayLocation].wrapper != null ||
      guildStyles[props.displayLocation].wrapper != null
    ) {
      return ({ children }: React.PropsWithChildren) => {
        return (
          <div
            className={guildClasses[props.displayLocation].wrapper}
            style={guildStyles[props.displayLocation].wrapper}
          >
            {children}
          </div>
        );
      };
    }
  }, [guildClasses, guildStyles, props.displayLocation]);

  if (props.isVisible === false) {
    return null;
  }

  if (selectedRole == null) {
    return null;
  }

  const RoleIcon = GUILD_ROLES_EMOJIS[selectedRole.id];
  const content = (
    <span
      className={guildClasses[props.displayLocation].span}
      style={{ ...guildStyles[props.displayLocation].span, display: "flex", alignItems: "center" }}
    >
      {selectedRole.name}
      {RoleIcon && <RoleIcon width={18} height={18} color={theme.font.secondary.default} />}
    </span>
  );

  return (
    <>
      {Wrapper ? <Wrapper>{content}</Wrapper> : content}
      {modal === MODAL_SELECTORS.WALLET_VIEW && selectedRole != null && (
        <div className="sp12"></div>
      )}
    </>
  );
};
